import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
  ApiOutlined,
  CloudServerOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  BarChartOutlined,
} from '@ant-design/icons';

/**
 * 偽裝首頁 — OKTech Solutions API 管理平台
 * 完全不暴露任何 VPN 相關資訊
 */
export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [logoClicks, setLogoClicks] = useState(0);
  const [isDark] = useState(() => {
    try {
      return localStorage.getItem('theme-mode') === 'dark';
    } catch {
      return false;
    }
  });

  // 連續點擊 logo 5 次跳轉到 /manage
  const handleLogoClick = useCallback(() => {
    setLogoClicks((prev) => {
      const next = prev + 1;
      if (next >= 5) {
        navigate('/manage');
        return 0;
      }
      // 2 秒後重置計數
      setTimeout(() => setLogoClicks(0), 2000);
      return next;
    });
  }, [navigate]);

  const colors = isDark
    ? {
        bg: '#0a0a0a',
        surface: '#141414',
        surfaceBorder: '#1f1f1f',
        text: '#e5e5e5',
        textSecondary: '#8c8c8c',
        textTertiary: '#595959',
        accent: '#1668dc',
        accentLight: 'rgba(22,104,220,0.08)',
        gradientStart: '#0a0a0a',
        gradientEnd: '#0d1117',
        heroGradient: 'linear-gradient(135deg, #0d1117 0%, #0a0a0a 50%, #111827 100%)',
        cardBg: '#141414',
        footerBg: '#0d0d0d',
      }
    : {
        bg: '#ffffff',
        surface: '#fafafa',
        surfaceBorder: '#f0f0f0',
        text: '#1a1a1a',
        textSecondary: '#595959',
        textTertiary: '#8c8c8c',
        accent: '#1677ff',
        accentLight: 'rgba(22,119,255,0.06)',
        gradientStart: '#ffffff',
        gradientEnd: '#f5f7fa',
        heroGradient: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 50%, #e8f4f8 100%)',
        cardBg: '#ffffff',
        footerBg: '#fafafa',
      };

  const features = [
    {
      icon: <ThunderboltOutlined style={{ fontSize: 28, color: colors.accent }} />,
      title: 'High Performance',
      desc: 'Sub-millisecond latency with distributed edge caching and intelligent load balancing.',
    },
    {
      icon: <SafetyCertificateOutlined style={{ fontSize: 28, color: colors.accent }} />,
      title: 'Enterprise Security',
      desc: 'SOC 2 Type II compliant with end-to-end encryption, OAuth 2.0, and RBAC.',
    },
    {
      icon: <GlobalOutlined style={{ fontSize: 28, color: colors.accent }} />,
      title: 'Global Infrastructure',
      desc: 'Points of presence in 40+ regions for low-latency API delivery worldwide.',
    },
    {
      icon: <CloudServerOutlined style={{ fontSize: 28, color: colors.accent }} />,
      title: 'Auto Scaling',
      desc: 'Automatically scales from zero to millions of requests without configuration.',
    },
    {
      icon: <BarChartOutlined style={{ fontSize: 28, color: colors.accent }} />,
      title: 'Real-time Analytics',
      desc: 'Monitor API usage, error rates, and performance metrics in real-time dashboards.',
    },
    {
      icon: <ApiOutlined style={{ fontSize: 28, color: colors.accent }} />,
      title: 'Developer Tools',
      desc: 'RESTful APIs, SDKs for 12 languages, OpenAPI specs, and interactive documentation.',
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, color: colors.text }}>
      {/* Nav */}
      <nav
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 40px',
          maxWidth: 1200,
          margin: '0 auto',
        }}
      >
        <div
          onClick={handleLogoClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'default',
            userSelect: 'none',
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: `linear-gradient(135deg, ${colors.accent}, #4096ff)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            O
          </div>
          <span style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>
            OKTech
          </span>
        </div>
        <div style={{ display: 'flex', gap: 32, alignItems: 'center' }}>
          <a href="#features" style={{ color: colors.textSecondary, textDecoration: 'none', fontSize: 14 }}>
            Features
          </a>
          <a href="#pricing" style={{ color: colors.textSecondary, textDecoration: 'none', fontSize: 14 }}>
            Pricing
          </a>
          <a
            href="https://docs.example.com"
            style={{ color: colors.textSecondary, textDecoration: 'none', fontSize: 14 }}
            onClick={(e) => e.preventDefault()}
          >
            Docs
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section
        style={{
          background: colors.heroGradient,
          padding: '100px 40px 80px',
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div
            style={{
              display: 'inline-block',
              padding: '4px 14px',
              borderRadius: 20,
              background: colors.accentLight,
              color: colors.accent,
              fontSize: 13,
              fontWeight: 500,
              marginBottom: 24,
              border: `1px solid ${isDark ? 'rgba(22,104,220,0.2)' : 'rgba(22,119,255,0.15)'}`,
            }}
          >
            Now available in Asia Pacific
          </div>
          <h1
            style={{
              fontSize: 'clamp(32px, 5vw, 52px)',
              fontWeight: 700,
              lineHeight: 1.15,
              letterSpacing: '-0.025em',
              margin: '0 0 20px',
              color: colors.text,
            }}
          >
            API Management
            <br />
            <span style={{ color: colors.accent }}>Built for Scale</span>
          </h1>
          <p
            style={{
              fontSize: 'clamp(16px, 2vw, 19px)',
              color: colors.textSecondary,
              lineHeight: 1.65,
              margin: '0 0 36px',
              maxWidth: 560,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            Deploy, monitor, and secure your APIs across global infrastructure.
            Trusted by 2,000+ teams from startups to enterprises.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              style={{
                padding: '12px 28px',
                borderRadius: 8,
                background: colors.accent,
                color: '#fff',
                border: 'none',
                fontSize: 15,
                fontWeight: 500,
                cursor: 'pointer',
              }}
              onClick={(e) => e.preventDefault()}
            >
              Start Free Trial
            </button>
            <button
              style={{
                padding: '12px 28px',
                borderRadius: 8,
                background: 'transparent',
                color: colors.text,
                border: `1px solid ${colors.surfaceBorder}`,
                fontSize: 15,
                fontWeight: 500,
                cursor: 'pointer',
              }}
              onClick={(e) => e.preventDefault()}
            >
              View Documentation
            </button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" style={{ padding: '80px 40px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <h2
            style={{
              fontSize: 28,
              fontWeight: 600,
              margin: '0 0 12px',
              letterSpacing: '-0.01em',
            }}
          >
            Everything you need
          </h2>
          <p style={{ color: colors.textSecondary, fontSize: 16, margin: 0 }}>
            A complete platform for API lifecycle management
          </p>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 24,
          }}
        >
          {features.map((f, i) => (
            <div
              key={i}
              style={{
                padding: 28,
                borderRadius: 12,
                background: colors.cardBg,
                border: `1px solid ${colors.surfaceBorder}`,
                transition: 'box-shadow 0.2s',
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  background: colors.accentLight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 16,
                }}
              >
                {f.icon}
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 8px' }}>{f.title}</h3>
              <p style={{ color: colors.textSecondary, fontSize: 14, lineHeight: 1.6, margin: 0 }}>
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section
        id="pricing"
        style={{
          padding: '60px 40px',
          background: colors.surface,
          borderTop: `1px solid ${colors.surfaceBorder}`,
          borderBottom: `1px solid ${colors.surfaceBorder}`,
        }}
      >
        <div
          style={{
            maxWidth: 900,
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 32,
            textAlign: 'center',
          }}
        >
          {[
            { value: '99.99%', label: 'Uptime SLA' },
            { value: '40+', label: 'Edge Locations' },
            { value: '<10ms', label: 'Avg Latency' },
            { value: '2,000+', label: 'Teams Served' },
          ].map((s, i) => (
            <div key={i}>
              <div style={{ fontSize: 32, fontWeight: 700, color: colors.accent }}>{s.value}</div>
              <div style={{ fontSize: 14, color: colors.textSecondary, marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          padding: '40px 40px',
          background: colors.footerBg,
          borderTop: `1px solid ${colors.surfaceBorder}`,
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <div style={{ fontSize: 13, color: colors.textTertiary }}>
            &copy; 2026 OKTech Solutions Ltd. All rights reserved.
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            <a
              href="/privacy"
              style={{ fontSize: 13, color: colors.textTertiary, textDecoration: 'none' }}
              onClick={(e) => e.preventDefault()}
            >
              Privacy
            </a>
            <a
              href="/terms"
              style={{ fontSize: 13, color: colors.textTertiary, textDecoration: 'none' }}
              onClick={(e) => e.preventDefault()}
            >
              Terms
            </a>
            {/* 隱藏入口：點擊 "System Status" 跳轉到 /manage */}
            <a
              href="/manage"
              style={{ fontSize: 13, color: colors.textTertiary, textDecoration: 'none' }}
              onClick={(e) => {
                e.preventDefault();
                navigate('/manage');
              }}
            >
              System Status
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
